/**
 * `/admin/ext/:name/*` (HUB-103). Sits inside `AdminLayout` (header + sidebar
 * are the shell's), checks the hub's enabled list, then renders the declared
 * settings page at `settings` and the extension's lazy admin module for
 * everything else. The first `React.lazy`/`Suspense` in this SPA — the
 * boundary is per page so a failed chunk never blanks the shell.
 */
import { Suspense, type ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useParams } from 'react-router';
import { Alert } from '@/components/ui/alert';
import { ExtensionSettings } from '@/pages/extensions/ExtensionSettings';
import { extensionDisplayName } from './extension-nav';
import { lazyExtensionAdmin } from './registry';
import { ExtensionAdminProvider } from './use-extension-admin';
import { useEnabledExtensions, type EnabledExtension } from './use-extensions';

export interface ExtensionRouteState {
  name: string;
  extensions: EnabledExtension[] | undefined;
  isLoading: boolean;
  error: unknown;
}

function NotEnabled({ name }: { name: string }) {
  return (
    <Alert variant="warning">
      Extension <span className="font-mono">{name}</span> is not enabled on this hub, or ships no admin
      page. <Link to="/admin" className="underline">Back to the dashboard</Link>
    </Alert>
  );
}

/** Pure render decision (the `renderGate` precedent) — testable without hooks. */
export function renderExtensionRoute({ name, extensions, isLoading, error }: ExtensionRouteState): ReactNode {
  if (isLoading) return null;
  if (error) return <Alert variant="destructive">Could not load extensions: {(error as Error).message}</Alert>;
  const ext = extensions?.find((e) => e.name === name);
  if (!ext) return <NotEnabled name={name} />;
  const Page = lazyExtensionAdmin(name);
  return (
    <ExtensionAdminProvider name={name}>
      <Routes>
        {ext.hasSettings && <Route path="settings" element={<ExtensionSettings name={name} />} />}
        <Route
          path="*"
          element={
            Page ? (
              <Suspense fallback={<p className="text-sm text-muted-foreground">Loading {extensionDisplayName(name)}…</p>}>
                <Page />
              </Suspense>
            ) : ext.hasSettings ? (
              <Navigate to="settings" replace />
            ) : (
              <NotEnabled name={name} />
            )
          }
        />
      </Routes>
    </ExtensionAdminProvider>
  );
}

export function ExtensionAdminRoute() {
  const { name = '' } = useParams();
  const { data, isLoading, error } = useEnabledExtensions();
  return renderExtensionRoute({ name, extensions: data, isLoading, error });
}
